import React, { memo, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { FilterType } from "./reportsUtils";
import "../styles/FilterSortHeader.css";

interface FilterSortHeaderProps {
  selectedFilter: FilterType;
  onFilterChange: (filter: FilterType) => void;
  searchTerm: string;
  onSearchChange: (term: string) => void;
  reportsCount: number;
  onClose?: () => void;
  userFilter?: string | null;
  onUserFilterChange?: (nickname: string | null) => void;
  showScrollToTop?: boolean;
  onScrollToTop?: () => void;
}

const FilterSortHeader: React.FC<FilterSortHeaderProps> = memo(
  ({
    selectedFilter,
    onFilterChange,
    searchTerm,
    onSearchChange,
    reportsCount,
    onClose,
    userFilter,
    onUserFilterChange,
    showScrollToTop,
    onScrollToTop,
  }) => {
    const { t } = useTranslation();
    const [showFilterMenu, setShowFilterMenu] = useState(false);
    const [showSearchInput, setShowSearchInput] = useState(false);

    const filterLabels: Record<FilterType, string> = {
      all: t("filter.all"),
      "not-cleaned": t("filter.needsCleaning"),
      cleaned: t("filter.cleaned"),
    };

    const handleFilterSelect = useCallback(
      (filter: FilterType) => {
        onFilterChange(filter);
        setShowFilterMenu(false);
      },
      [onFilterChange]
    );

    const handleSearchToggle = useCallback(() => {
      setShowSearchInput((prev) => !prev);
      if (showSearchInput && searchTerm) {
        onSearchChange("");
      }
    }, [showSearchInput, searchTerm, onSearchChange]);

    const handleSearchChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        onSearchChange(e.target.value);
      },
      [onSearchChange]
    );

    const handleClearSearch = useCallback(() => {
      onSearchChange("");
    }, [onSearchChange]);

    const handleOverlayClick = useCallback(() => {
      setShowFilterMenu(false);
    }, []);

    const currentFilterLabel = filterLabels[selectedFilter];

    return (
      <>
        <div className="filter-sort-header">
          <div className="header-title-row">
            <h3 className="reports-title">{t("report.reportsCount", { count: reportsCount })}</h3>
            {onClose && (
              <button
                className="header-close-button"
                onClick={onClose}
                aria-label={t("filter.closeSidebar")}
              >
                ×
              </button>
            )}
          </div>

          <div className="filter-sort-buttons">
            {/* Filter Button */}
            <button
              className="filter-sort-btn"
              onClick={() => setShowFilterMenu(!showFilterMenu)}
            >
              <img
                src={`${process.env.PUBLIC_URL}/images/filter.png`}
                alt={t("filter.filter")}
                className="btn-icon"
              />
              <span className="btn-text">{currentFilterLabel}</span>
            </button>

            {/* User Filter Button */}
            {userFilter && onUserFilterChange && (
              <button
                className="filter-sort-btn user-filter-btn"
              >
                <span className="btn-text">@{userFilter}</span>
                <button
                  className="clear-user-filter-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    onUserFilterChange(null);
                  }}
                  aria-label={t("filter.clearUserFilter")}
                >
                  ✕
                </button>
              </button>
            )}

            {/* Search Toggle Button */}
            <button
              className={`filter-sort-btn search-btn ${showSearchInput ? "active" : ""}`}
              onClick={handleSearchToggle}
            >
              <img
                src={`${process.env.PUBLIC_URL}/images/search.png`}
                alt={t("filter.search")}
                className="btn-icon"
              />
            </button>
          </div>
        </div>

        {/* Scroll To Top Button - Fixed in corner */}
        {showScrollToTop && onScrollToTop && (
          <button
            className="scroll-to-top-btn"
            onClick={onScrollToTop}
            aria-label={t("filter.scrollToTop")}
            title={t("filter.backToTop")}
          >
            <span className="scroll-arrow">↑</span>
          </button>
        )}

        {/* Search Input - Slides down when active */}
        {showSearchInput && (
          <div className="search-input-container">
            <div className="search-input-wrapper">
              <input
                type="text"
                placeholder={t("filter.searchPlaceholder")}
                value={searchTerm}
                onChange={handleSearchChange}
                className="search-input"
                autoFocus
              />
              {searchTerm && (
                <button
                  className="clear-search-button"
                  onClick={handleClearSearch}
                  title={t("filter.clearSearch")}
                  aria-label={t("filter.clearSearch")}
                >
                  ✕
                </button>
              )}
            </div>
          </div>
        )}

        {/* Filter Dropdown Menu */}
        {showFilterMenu && (
          <>
            <div className="filter-menu-overlay" onClick={handleOverlayClick} />
            <div className="filter-dropdown-menu">
              <button
                className={`filter-menu-item ${selectedFilter === "all" ? "active" : ""}`}
                onClick={() => handleFilterSelect("all")}
              >
                {filterLabels.all}
              </button>
              <button
                className={`filter-menu-item ${
                  selectedFilter === "not-cleaned" ? "active" : ""
                }`}
                onClick={() => handleFilterSelect("not-cleaned")}
              >
                {filterLabels["not-cleaned"]}
              </button>
              <button
                className={`filter-menu-item ${
                  selectedFilter === "cleaned" ? "active" : ""
                }`}
                onClick={() => handleFilterSelect("cleaned")}
              >
                {filterLabels.cleaned}
              </button>
            </div>
          </>
        )}
      </>
    );
  }
);

FilterSortHeader.displayName = "FilterSortHeader";

export default FilterSortHeader;

